// src/components/panoramica/PlayerSheet.js
// Dettaglio della previsione di un giocatore (pannello laterale su desktop,
// "bottom sheet" su telefono): come nasce il numero, fattore per fattore.
import React, { useEffect } from 'react';
import { X, Ban, RotateCcw } from 'lucide-react';
import TeamBadge from '../TeamBadge';
import { RolePills } from '../auction/AuctionFormationTab';
import { getPurchaseName, getPurchaseTeam } from '../auction/panoramicaEngine';
import { formatFixtureDate } from '../../data/serieACalendar';
import { RangeBar, Sparkline } from './NextMatchList';

const pct = (x) => `${Math.round(x * 100)}%`;

export default function PlayerSheet({ item, rolesMode, isStarter, isCaptain, isVice, manualOut, onClose, onMarkOut, onMarkIn, busy }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;
  const { purchase, forecast: f } = item;
  const fx = f.fixture;
  const max = Math.max(0.5, ...f.factors.map((x) => Math.abs(x.value)));

  return (
    <div className="pz-sheet-backdrop" onClick={onClose}>
      <aside className="pz-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Previsione ${getPurchaseName(purchase)}`}>
        <div className="pz-sheet__grab" aria-hidden="true" />
        <div className="pz-sheet__head">
          <TeamBadge team={getPurchaseTeam(purchase)} size={40} showTooltip={false} />
          <div>
            <h3>{getPurchaseName(purchase)}</h3>
            <span className="pz-sheet__sub">
              <RolePills purchase={purchase} rolesMode={rolesMode} />
              {getPurchaseTeam(purchase)}
              {isStarter && <em className="pz-tag-xi">{isCaptain ? 'Capitano' : isVice ? 'Vice' : 'Titolare'}</em>}
            </span>
          </div>
          <button type="button" className="pz-sheet__close" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>
        </div>

        {fx && (
          <div className="pz-sheet__match">
            <TeamBadge team={fx.opponent} size={22} showTooltip={false} />
            <span><b>{fx.home ? 'In casa con' : 'In trasferta con'} {fx.opponent}</b> · G{fx.giornata} · {formatFixtureDate(fx.date)}</span>
          </div>
        )}

        <div className="pz-sheet__big">
          <div>
            <span>Fantavoto atteso</span>
            <b>{f.pPlay === 0 ? '—' : f.expFV.toFixed(1)}</b>
            <em>forchetta {f.p10.toFixed(1)}–{f.p90.toFixed(1)}</em>
          </div>
          <RangeBar f={f} />
        </div>

        <div className="pz-sheet__grid">
          <div><span>Gioca</span><b>{pct(f.pPlay)}</b></div>
          <div><span>Voto atteso</span><b>{f.expMark.toFixed(2)}</b></div>
          {f.role !== 'P' && <div><span>Gol</span><b>{pct(f.pGoal)}</b></div>}
          <div><span>Assist</span><b>{pct(f.pAssist)}</b></div>
          {(f.role === 'P' || f.role === 'D') && <div><span>Porta inviolata</span><b>{pct(f.pCleanSheet)}</b></div>}
          <div><span>Gol attesi squadra</span><b>{f.lambdaFor.toFixed(2)}</b></div>
          <div><span>Gol attesi avversario</span><b>{f.lambdaAgainst.toFixed(2)}</b></div>
          <div><span>Presenze</span><b>{f.apps}{f.apps ? ` (${f.starts} da titolare)` : ''}</b></div>
        </div>

        <h4 className="pz-sheet__h">Da dove viene il numero</h4>
        <ul className="pz-factors">
          {f.factors.map((x) => {
            const w = Math.min(50, (Math.abs(x.value) / max) * 50);
            return (
              <li key={x.key}>
                <span>{x.label}</span>
                <span className="pz-duel__track">
                  <i className={x.value >= 0 ? 'is-pos' : 'is-neg'} style={x.value >= 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
                </span>
                <b className={x.value >= 0 ? 'is-pos' : 'is-neg'}>{x.value >= 0 ? '+' : ''}{x.value.toFixed(2)}</b>
              </li>
            );
          })}
        </ul>
        <p className="pz-sheet__note">
          Rispetto a un giocatore medio del ruolo. Il voto pesa di più le ultime partite; l'avversario è stimato sui
          risultati reali della Serie A (gol fatti e subiti), i bonus dalla quota di gol/assist del giocatore sulla
          sua squadra.
        </p>

        <h4 className="pz-sheet__h">Ultimi fantavoti e previsione</h4>
        <div className="pz-sheet__trend">
          <Sparkline last={f.last} forecast={f.expFV} />
          <span>{f.last.length ? f.last.map((l) => l.fmark.toFixed(1)).join(' · ') : 'Nessun voto in stagione'}</span>
        </div>

        <div className="pz-sheet__actions">
          {manualOut ? (
            <button type="button" className="pz-btn" onClick={onMarkIn} disabled={busy}><RotateCcw size={14} /> Gioca: annulla la segnalazione</button>
          ) : (
            <button type="button" className="pz-btn pz-btn--warn" onClick={onMarkOut} disabled={busy}><Ban size={14} /> So per certo che non gioca</button>
          )}
        </div>
      </aside>
    </div>
  );
}
